import { Button, message, Typography } from "antd";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useUserFilesTabs } from "./UserFilesTabsContext";
import "./ConselorTakeAway.scss";
import { articlesSVC } from "../../../services";
import useSWRMutation from "swr/mutation";
import { patchPostFetcher } from "../../../utils/fetcher";
import { mutate } from "swr";
import { useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { AppModal, AppUpload } from "../../../components/common";

const { Text } = Typography;

type Option = { label: string; value: string; image?: string };
type TakeawayStatusPayload = { status: "approved" | "rejected" };

const IMMERSIVE_CARE_OPTIONS: Option[] = [
  { label: "Crisis Care", value: "Crisis Care" },
  { label: "Quick-Fix", value: "Quick-Fix" },
  { label: "Daily Care", value: "Daily Care" },
  { label: "Daily Affirmations", value: "Daily Affirmations" },
  { label: "Be Healthy", value: "Be Healthy" },
  { label: "Be Happy", value: "Be Happy" },
  { label: "Be Fearless", value: "Be Fearless" },
  { label: "Be Creative", value: "Be Creative" },
  { label: "Sleep Easy", value: "Sleep Easy" },
  { label: "Be Social", value: "Be Social" },
];

const LIVE_WELLNESS_OPTIONS: Option[] = [
  { label: "Wellness Chat", value: "Wellness Chat" },
  { label: "Wellness Counseling", value: "Wellness Counseling" },
];

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ indent: "-1" }, { indent: "+1" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "bold", "italic", "underline", "strike",
  "color", "background",
  "align", "indent",
];

function HashtagSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Option[];
  value: string[];
  onChange: (vals: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (val: string) =>
    onChange(
      value.includes(val) ? value.filter((v) => v !== val) : [...value, val],
    );

  const selectedLabels = options.filter((o) => value.includes(o.value));

  return (
    <div className="takeaway-field" ref={ref}>
      <p className="takeaway-field__label">{label}</p>
      <div
        className={`takeaway-select ${open ? "takeaway-select--open" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="takeaway-select__inner">
          {selectedLabels.length === 0 ? (
            <span className="takeaway-select__placeholder">
              Choose an option...
            </span>
          ) : (
            <div className="takeaway-select__tags">
              {selectedLabels.map((o) => (
                <span key={o.value} className="takeaway-select__tag">
                  {o.label}
                  <button
                    className="takeaway-select__tag-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(o.value);
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <span
            className={`takeaway-select__arrow ${open ? "takeaway-select__arrow--up" : ""}`}
          >
            ▾
          </span>
        </div>
        {open && (
          <div
            className="takeaway-select__dropdown"
            onClick={(e) => e.stopPropagation()}
          >
           {options.map((o) => (
  <div
    key={o.value}
    className={`takeaway-select__option ${value.includes(o.value) ? "takeaway-select__option--selected" : ""}`}
    onClick={() => toggle(o.value)}
  >
    {o.image && (
      <img
        src={o.image}
        alt={o.label}
        className="takeaway-select__option-img"
      />
    )}
    <span>{o.label}</span>
  </div>
))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldInput({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="takeaway-field">
      <p className="takeaway-field__label">{label}</p>
      <input
        className="takeaway-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function FilePicker({
  label,
  file,
  onChange,
  existingUrl,
}: {
  label: string;
  file: File | null;
  onChange: (f: File | null) => void;
  existingUrl?: string;
}) {
  const previewUrl = file ? URL.createObjectURL(file) : (existingUrl ?? null);

  return (
    <div className="takeaway-field">
      <p className="takeaway-field__label">{label}</p>
      <AppUpload
        kind="image"
        uploadedImage={previewUrl}
        handleRemove={previewUrl ? () => onChange(null) : undefined}
        handleFileUpload={(e) => {
          const selected = e.target.files?.[0] ?? null;
          if (selected && selected.size > 10 * 1024 * 1024) {
            message.error("Image size should not exceed 10MB");
            e.target.value = "";
            return;
          }
          onChange(selected);
        }}
      />
    </div>
  );
}

interface ArticleEntry {
  articleUrl: string;
  articleTitle: string;
  articleDescription: string;
  thumbnailUrl?: string;
  thumbnail: File | null;
}

const EMPTY_ARTICLE: ArticleEntry = {
  articleUrl: "",
  articleTitle: "",
  articleDescription: "",
  thumbnail: null,
  
  thumbnailUrl: ""
};

function ArticleCard({
  article,
  onChange,
}: {
  article: ArticleEntry;
  onChange: (a: ArticleEntry) => void;
}) {
  return (
    <div className="takeaway-article-card take_away_alt">
      <FieldInput
        label="Article URL"
        placeholder="Paste the article URL here"
        value={article.articleUrl}
        onChange={(v) => onChange({ ...article, articleUrl: v })}
      />
      <FieldInput
        label="Article Title"
        placeholder="Enter the article title"
        value={article.articleTitle}
        onChange={(v) => onChange({ ...article, articleTitle: v })}
      />
      <FieldInput
        label="Article Description"
        placeholder="Write a brief description"
        value={article.articleDescription}
        onChange={(v) => onChange({ ...article, articleDescription: v })}
      />
     <FilePicker
  label="Thumbnail Image"
  file={article.thumbnail}
  onChange={(f) =>
    onChange({
      ...article,
      thumbnail: f,
      thumbnailUrl: f ? article.thumbnailUrl : "" // clear old image if deleted
    })
  }
  existingUrl={article.thumbnailUrl}
/>
    </div>
  );
}

export default function ConselorTakeAway() {
  const {
    setSeniorcounselorTakeAwayModal,
    seniorCounselorObj,
    seniorcounselorTakeAwayModal,
  } = useUserFilesTabs();

  console.log(seniorCounselorObj, "seniorCounselorObjseniorCounselorObj");

  // ─── State ────────────────────────────────────────────────────────────────
  const [immersiveCare, setImmersiveCare] = useState<string[]>([]);
  const [liveWellness, setLiveWellness] = useState<string[]>([]);
  const [themesSoulsara, setThemesSoulsara] = useState<string[]>([]);
  const [soulsaraTimes, setSoulsaraTimes] = useState<string[]>([]);
  const [eventVideos, setEventVideos] = useState<string[]>([]);
  const [articles, setArticles] = useState<ArticleEntry[]>([{ ...EMPTY_ARTICLE }]);
  const [reportSummary, setReportSummary] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [themesOptions, setThemesOptions] = useState<Option[]>([]);
  const [timesOptions, setTimesOptions] = useState<Option[]>([]);
  const [videosOptions, setVideosOptions] = useState<Option[]>([]);

  const API_URL = process.env.REACT_APP_API_URL;

  // ─── Load dropdown options ────────────────────────────────────────────────
  useEffect(() => {
   const toOptions = (data: any[]) =>
  data?.map((i) => ({ label: i.title, value: i._id, image: i.thumbnailImage ?? i.image ?? "" })) ?? [];
    articlesSVC.getThemeposts().then((res: any) => setThemesOptions(toOptions(res?.data)));
    articlesSVC.getTimesList().then((res: any) => setTimesOptions(toOptions(res?.data)));
    articlesSVC.getVideoList().then((res: any) => setVideosOptions(toOptions(res?.data?.data)));
  }, []);

  // ─── Populate form from existing data ────────────────────────────────────
  useEffect(() => {
    const obj = seniorCounselorObj;

    if (!obj || Object.keys(obj).length === 0) {
      setImmersiveCare([]);
      setLiveWellness([]);
      setThemesSoulsara([]);
      setSoulsaraTimes([]);
      setEventVideos([]);
      setArticles([{ ...EMPTY_ARTICLE }]);
      setReportSummary("");
      setIsDirty(false);
      return;
    }

    setImmersiveCare(obj.immersiveCare ?? []);
    setLiveWellness(obj.liveWellnessSupport ?? []);
    setThemesSoulsara(obj.themes?.map((t: any) => t) ?? []);
    setSoulsaraTimes(obj.times?.map((t: any) => t) ?? []);
  setEventVideos(
  obj.eventVideos?.map((v: any) => {
    if (typeof v === "string") return v;

    return Object.keys(v)
      .filter((k) => !isNaN(Number(k))) 
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => v[k])
      .join("");
  }) ?? []
);
  setArticles(
  obj.externalArticles?.length
    ? obj.externalArticles.map((a: any) => ({
        articleUrl: a.articleUrl ?? "",
        articleTitle: a.articleTitle ?? "",
        articleDescription: a.articleDescription ?? "",
        thumbnail: null,
        thumbnailUrl: a.thumbnailImage ?? "", // ← add this
      }))
    : [{ ...EMPTY_ARTICLE }],
);
    setReportSummary(obj.reportSummary ?? "");
    setIsDirty(false);
  }, [seniorCounselorObj, seniorcounselorTakeAwayModal]);

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const markDirty = (setter: (val: any) => void) => (val: any) => {
    setter(val);
    setIsDirty(true);
  };

  // ─── Build payload ────────────────────────────────────────────────────────
const buildPayload = () => {
  const formData = new FormData();

  immersiveCare.forEach((item) => {
    formData.append("immersiveCare[]", item);
  });

  liveWellness.forEach((item) => {
    formData.append("liveWellnessSupport[]", item);
  });

  // THEMES
  themesSoulsara.forEach((themeId) => {
    formData.append("themes[]", themeId);
  });

  // TIMES
  soulsaraTimes.forEach((timeId) => {
    formData.append("times[]", timeId);
  });

  // EVENT VIDEOS
 eventVideos.forEach((videoId, index) => {
  formData.append(`eventVideos[${index}]`, videoId);
});

  formData.append("reportSummary", reportSummary);

  const filteredArticles = articles.filter(
    (a) => a.articleUrl || a.articleTitle
  );

  filteredArticles.forEach((article, index) => {
    formData.append(`externalArticles[${index}][articleTitle]`, article.articleTitle);
    formData.append(`externalArticles[${index}][articleUrl]`, article.articleUrl);
    formData.append(`externalArticles[${index}][articleDescription]`, article.articleDescription);
    formData.append(
  `externalArticles[${index}][thumbnailImage]`,
  article.thumbnailUrl ?? "" 
);

    if (article.thumbnail) {
      formData.append(`externalThumbnail_${index}`, article.thumbnail);
    }
  });

  return formData;
};

  const { trigger: sessionReportTrigger } = useSWRMutation<
    any,
    any,
    string,
    TakeawayStatusPayload
  >(
    `${API_URL}/api/takeaway/${seniorCounselorObj?.takeawayId}/status`,
    (url: string, { arg }: { arg: TakeawayStatusPayload }) =>
      patchPostFetcher({ url, data: arg, method: "PUT" }),
  );

  const location = useLocation();
  const clientId = location?.pathname.split("/")[3];

  const handleSave = async (status: TakeawayStatusPayload["status"]) => {
    try {
      await sessionReportTrigger({ status });
      message.success("Status updated");
      setSeniorcounselorTakeAwayModal(false);
      mutate(`${API_URL}/api/takeaway/${clientId}`);
    } catch (err) {
      message.error("Failed to update status");
      console.error(err);
    }
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    articlesSVC
      .updateTakeway(seniorCounselorObj?.takeawayId, buildPayload())
      .then(() => {
        setSeniorcounselorTakeAwayModal(false);
        message.success("Takeaway submitted successfully");
        mutate(`${API_URL}/api/takeaway/${clientId}`);
      })
      .catch(() => {
        message.warning("Takeaway already submitted for this session");
        setSeniorcounselorTakeAwayModal(false);
      })
      .finally(() => setSubmitting(false));
  };

  const canSubmit = isDirty && !submitting;
  const isPending = seniorCounselorObj?.approvalStatus === "pending";

  return (
    <AppModal
      className="takeawayModal"
      title="Review Takeaway Resources"
      footer={
        <>
          <Button danger disabled={!isPending} onClick={() => handleSave("rejected")}>
            Reject
          </Button>
          <Button disabled={!canSubmit || !isPending} onClick={handleSubmit}>
            Edit & Submit
          </Button>
          <Button type="primary" disabled={!isPending} onClick={() => handleSave("approved")}>
            Approve
          </Button>
        </>
      }
      open={seniorcounselorTakeAwayModal}
      onCancel={() => setSeniorcounselorTakeAwayModal(false)}
    >
      <p className="takeaway-section-label">Our Solutions</p>
      <div className="takeaway-section-box">
        <HashtagSelect
          label="ImmersiveCARE"
          options={IMMERSIVE_CARE_OPTIONS}
          value={immersiveCare}
          onChange={markDirty(setImmersiveCare)}
        />
        <HashtagSelect
          label="LIVE Wellness Support"
          options={LIVE_WELLNESS_OPTIONS}
          value={liveWellness}
          onChange={markDirty(setLiveWellness)}
        />
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">Internal Articles</p>
      <div className="takeaway-section-box">
        <HashtagSelect
          label="THEMES@SOULSARA"
          options={themesOptions}
          value={themesSoulsara}
          onChange={markDirty(setThemesSoulsara)}
        />
        <HashtagSelect
          label="SOULSARA Times"
          options={timesOptions}
          value={soulsaraTimes}
          onChange={markDirty(setSoulsaraTimes)}
        />
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">External Articles</p>
      {articles.map((article, i) => (
        <ArticleCard
          key={i}
          article={article}
          onChange={(updated) => {
            setIsDirty(true);
            setArticles((prev) => prev.map((a, idx) => (idx === i ? updated : a)));
          }}
        />
      ))}
      <div className="takeaway-add-another">
        <button
          className="takeaway-add-btn"
          onClick={() => {
            setIsDirty(true);
            setArticles((prev) => [...prev, { ...EMPTY_ARTICLE }]);
          }}
        >
          Add Another Article
        </button>
      </div>

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">Our Event Videos</p>
      <HashtagSelect
        label="Event Video(s)"
        options={videosOptions}
        value={eventVideos}
        onChange={markDirty(setEventVideos)}
      />

      <div className="takeaway-divider" />

      <p className="takeaway-section-label">Additional Comments</p>
      <div className="takeaway-field react_quill">
        <ReactQuill
          theme="snow"
          value={reportSummary}
          onChange={markDirty(setReportSummary)}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Type your additional comments here..."
          className="takeaway-quill"
        />
      </div>

      <div className="noteBox">
        <Text className="noteTitle">Note:</Text>
        <Text className="noteText">
          Once approved, the report will be shared directly with the user!
        </Text>
      </div>
    </AppModal>
  );
}